import {
  ChartFrame,
  type ChartStripLine,
  dataVisColorTokens,
  LineChart,
} from "@assetwatch/charts";
import {
  Button,
  Card,
  CardBorderFill,
  Chip,
  Group,
  Modal,
  Progress,
  Stack,
  Text,
  Tile,
  Title,
} from "@assetwatch/ui";
import { useState } from "react";
import type {
  FacilityDetail as FacilityDetailData,
  FactorDefinition,
  GetFacilityResponse,
  Hub,
  HubFactor,
} from "./api";
import { FACTOR_KEYS } from "./api";
import {
  factorChart,
  factorFacts,
  formatCount,
  formatScore,
  hasData,
  SCORE_MARKS,
  scoreTone,
  seriesPoints,
} from "./lib";

type Meta = Pick<
  GetFacilityResponse,
  "day_labels" | "delay_unit" | "thresholds" | "definitions"
>;

/**
 * NOT A REUSED COMPONENT (flag in the PR): `Progress` has no tick marks, so the
 * 40 / 70 marks are two absolutely positioned lines laid over it. Everything
 * else about the bar is the library's.
 */
function ScoreBar({ score, label }: { score: number; label: string }) {
  const tone = scoreTone(score);

  return (
    <Stack gap={2}>
      <div className="relative">
        <Progress
          aria-label={label}
          size="lg"
          tone={tone === "neutral" ? "brand" : tone}
          value={score}
        />
        {SCORE_MARKS.map((mark) => (
          <span
            aria-hidden
            className="absolute -top-(--spacing-2) -bottom-(--spacing-2) w-px bg-surface-on-surface"
            key={mark}
            style={{ left: `${mark}%` }}
          />
        ))}
      </div>
      <div aria-hidden className="relative h-(--spacing-12)">
        {SCORE_MARKS.map((mark) => (
          <span
            className="absolute -translate-x-1/2"
            key={mark}
            style={{ left: `${mark}%` }}
          >
            <Text as="span" size="sm" tone="subtle">
              {mark}
            </Text>
          </span>
        ))}
      </div>
    </Stack>
  );
}

/** A score, its band word, "out of 100", and the marked bar. Null: "n/a", no bar. */
function ScoreMeter({
  label,
  score,
  band,
}: {
  label?: string;
  score: number | null;
  band: string;
}) {
  return (
    <Stack gap={4}>
      {label ? (
        <Text size="sm" tone="subtle" weight="medium">
          {label.toUpperCase()}
        </Text>
      ) : null}
      <Group align="end" gap={8} justify="between">
        <Group align="end" gap={8}>
          <Title size="xl" weight="bold">
            {formatScore(score)}
          </Title>
          <Chip label={band} tone={scoreTone(score)} />
        </Group>
        <Text size="sm" tone="subtle">
          out of 100
        </Text>
      </Group>
      {score === null ? null : (
        <ScoreBar
          label={`${label ?? "Score"}: ${formatScore(score)} out of 100, ${band}`}
          score={score}
        />
      )}
    </Stack>
  );
}

function FacilityHeader({
  facility,
  meta,
}: {
  facility: FacilityDetailData;
  meta: Meta;
}) {
  const { summary } = facility;

  return (
    <Tile hasBorder padding="comfortable" radius="lg" tone="muted">
      <Group align="start" gap={32} wrap="wrap">
        <div className="w-72">
          <ScoreMeter
            band={summary.band}
            label="Facility signal health"
            score={summary.health}
          />
        </div>
        <div className="min-w-0 flex-1">
          <Stack gap={8}>
            <Group align="end" gap={8} wrap="wrap">
              <Title size="lg" weight="bold">
                Facility
              </Title>
              <Text tone="subtle">{facility.id}</Text>
            </Group>
            <Text size="sm" tone="subtle" weight="medium">
              {formatCount(summary.hubs)} HUBS · {formatCount(summary.sensors)}{" "}
              SENSORS · WORST HUB {formatScore(summary.worst_hub)} ·{" "}
              {formatCount(summary.sole_served)} SOLE-SERVED SENSORS
            </Text>
            <Group gap={8} wrap="wrap">
              {FACTOR_KEYS.map((key) => {
                const score = summary.factors[key] ?? null;
                return (
                  <Tile hasBorder key={key} padding="dense" radius="md">
                    <Group gap={8}>
                      <Text size="sm">
                        {meta.definitions[key]?.title ?? key}
                      </Text>
                      <Chip
                        label={formatScore(score)}
                        tone={scoreTone(score)}
                      />
                    </Group>
                  </Tile>
                );
              })}
            </Group>
          </Stack>
        </div>
      </Group>
    </Tile>
  );
}

function FactorChart({
  factor,
  hub,
  meta,
}: {
  factor: HubFactor;
  hub: Hub;
  meta: Meta;
}) {
  const chart = factorChart(factor.key);

  if (!chart) {
    return (
      <Text size="sm" tone="subtle">
        {factor.title} is a single number, so it has no daily chart.
      </Text>
    );
  }

  const values = hub.series[chart.series];
  if (!hasData(values)) {
    return (
      <Text size="sm" tone="subtle">
        No daily data for this factor in the window.
      </Text>
    );
  }

  const unit = chart.unit(meta.delay_unit);
  // A chart's markers are always [good, zero] or none at all.
  const lineStyles = [
    ["good", dataVisColorTokens.criticalGreen],
    ["zero", dataVisColorTokens.criticalRed],
  ] as const;
  const stripLines: ChartStripLine[] = chart
    .markers(meta.thresholds)
    .map((marker, index) => ({
      id: lineStyles[index][0],
      axis: "y",
      value: marker.value,
      label: marker.label,
      colorToken: lineStyles[index][1],
      lineStyle: "shortDash",
    }));

  return (
    <ChartFrame subtitle={unit} title={`${chart.label} over the week`}>
      <LineChart
        emptyMessage="No daily data for this factor."
        height={240}
        pointMarkers
        series={[
          {
            id: `${hub.hub}-${chart.series}`,
            label: chart.label,
            data: seriesPoints(meta.day_labels, values),
            valueUnit: unit,
          },
        ]}
        stripLines={stripLines.length > 0 ? stripLines : undefined}
        xAxis={{
          tickCount: meta.day_labels.length,
          formatTick: (value) => meta.day_labels[Number(value)] ?? "",
        }}
        yAxis={{ title: unit, baseline: "zero" }}
      />
    </ChartFrame>
  );
}

function FactorPanel({
  factor,
  hub,
  meta,
  definition,
}: {
  factor: HubFactor;
  hub: Hub;
  meta: Meta;
  definition: FactorDefinition | undefined;
}) {
  const facts = factorFacts(factor.key, hub.facts, meta.delay_unit);

  // `why`, `measures`, `scale` and the definition text are generated in Python
  // from the live thresholds and rendered verbatim — never rebuilt here.
  return (
    <Stack gap={16}>
      <Stack gap={4}>
        <Text size="lg">{factor.why}</Text>
        <Text size="sm" tone="subtle">
          {factor.measures} Weight {formatScore(factor.weight)}. {factor.scale}
        </Text>
        {definition ? (
          <Text size="sm" tone="subtle">
            {definition.means}
          </Text>
        ) : null}
      </Stack>

      <Group align="start" gap={24} wrap="wrap">
        <div className="w-72">
          <Stack gap={12}>
            <ScoreMeter
              band={factor.band}
              label={`${factor.title} score`}
              score={factor.score}
            />
            <Stack gap={4}>
              {facts.map((row) => (
                <Group gap={8} justify="between" key={row.label}>
                  <Text size="sm" tone="subtle">
                    {row.label}
                  </Text>
                  <Text size="sm" weight="medium">
                    {row.value}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Stack>
        </div>
        <div className="min-w-0 flex-1">
          <FactorChart factor={factor} hub={hub} meta={meta} />
        </div>
      </Group>
    </Stack>
  );
}

function HubCard({
  hub,
  rank,
  onOpenFactor,
}: {
  hub: Hub;
  rank: number;
  onOpenFactor: (factorKey: string) => void;
}) {
  const tone = scoreTone(hub.health);

  return (
    <Card fillHeight padding="comfortable">
      {tone === "neutral" ? null : <CardBorderFill tone={tone} />}
      <Stack gap={16}>
        <Group align="start" gap={16} justify="between" wrap="wrap">
          <div className="w-56">
            <ScoreMeter band={hub.band} score={hub.health} />
          </div>
          <div className="min-w-0 flex-1">
            <Stack gap={4}>
              <Group gap={8} justify="between">
                <Text weight="medium">{hub.hub}</Text>
                <Text size="sm" tone="subtle">
                  #{rank}
                </Text>
              </Group>
              <Text size="sm" tone="subtle" weight="medium">
                {formatCount(hub.facts.sensors)} SENSORS ·{" "}
                {formatCount(hub.facts.readings)} READINGS
              </Text>
            </Stack>
          </div>
        </Group>

        {hub.factors.length === 0 ? (
          <Text size="sm" tone="subtle">
            This hub couldn’t be scored on any factor in this window.
          </Text>
        ) : (
          <>
            {/* Sparse on purpose: a factor the hub couldn't be scored on has
                no button, rather than a button showing zero. */}
            <Group gap={8} wrap="wrap">
              {hub.factors.map((factor) => (
                <Button
                  aria-label={`${factor.title}: ${formatScore(factor.score)}, ${factor.band}. View details`}
                  key={factor.key}
                  onClick={() => onOpenFactor(factor.key)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  {factor.title}
                  <Chip
                    label={formatScore(factor.score)}
                    tone={scoreTone(factor.score)}
                  />
                </Button>
              ))}
            </Group>
            <Text size="sm" tone="subtle">
              Click any factor to see why it scored that, and how it moved over
              the week.
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}

type OpenFactor = { hub: string; factor: string };

/** One facility: its header, then every hub as a card, worst health first. */
export function FacilityDetail({
  response,
}: {
  response: GetFacilityResponse;
}) {
  const { facility } = response;
  const [open, setOpen] = useState<OpenFactor | null>(null);

  const openHub = open
    ? facility.hubs.find((hub) => hub.hub === open.hub)
    : undefined;
  const openFactor = openHub?.factors.find((f) => f.key === open?.factor);

  return (
    <Stack gap={16}>
      <FacilityHeader facility={facility} meta={response} />
      <Text size="sm" tone="subtle">
        Hubs are ranked worst first. The facility score is sensor-weighted, so a
        large hub moves it more than a near-idle one. Click any factor on a card
        to see why it scored that way.
      </Text>
      {/* Machines are deferred: `machines` is always [] and isn't rendered, so
          the page doesn't imply a facility has none. */}
      <Title size="sm">Hubs ({facility.hubs.length.toLocaleString()})</Title>
      <div
        className="grid gap-(--spacing-16)"
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(22rem, 1fr))",
        }}
      >
        {facility.hubs.map((hub, index) => (
          <HubCard
            hub={hub}
            key={hub.hub}
            onOpenFactor={(factor) => setOpen({ hub: hub.hub, factor })}
            rank={index + 1}
          />
        ))}
      </div>

      {openHub && openFactor ? (
        <Modal
          description={`${openFactor.title} · ${formatScore(openFactor.score)} · ${openFactor.band}`}
          onOpenChange={(next) => {
            if (!next) {
              setOpen(null);
            }
          }}
          open
          size="lg"
          title={`Hub ${openHub.hub}`}
        >
          <FactorPanel
            definition={response.definitions[openFactor.key]}
            factor={openFactor}
            hub={openHub}
            meta={response}
          />
        </Modal>
      ) : null}
    </Stack>
  );
}
